import { z } from 'zod';

import { IsoDateTimeSchema, UuidSchema, BooleanQueryParamSchema } from './common.js';
import { PaginationQuerySchema, paginatedResponseSchema } from './pagination.js';

/**
 * Integration secrets store (Secrets Manager screen). Operator-managed
 * third-party credentials + env-style values, encrypted at rest with
 * AES-256-GCM. See ADR-0018.
 *
 * The plaintext value NEVER appears in list/detail responses - only a masked
 * `valuePreview` (last 4 chars). Retrieving the plaintext is the dedicated,
 * audited, ADMIN-only reveal endpoint.
 */

export const SecretKindSchema = z.enum([
  'API_KEY',
  'CREDENTIAL',
  'TOKEN',
  'WEBHOOK_SECRET',
  'ENV_VAR',
  'OTHER',
]);
export type SecretKind = z.infer<typeof SecretKindSchema>;

export const SecretProviderTypeSchema = z.enum([
  'FLIGHTS',
  'HOTELS',
  'TOURS',
  'ACTIVITIES',
  'PAYMENT',
  'MESSAGING',
  'OTHER',
]);
export type SecretProviderType = z.infer<typeof SecretProviderTypeSchema>;

export const SecretEnvironmentSchema = z.enum(['TEST', 'LIVE', 'ALL']);
export type SecretEnvironment = z.infer<typeof SecretEnvironmentSchema>;

/** Stable machine key, e.g. "tbo.api.password". */
export const SecretKeySchema = z
  .string()
  .trim()
  .min(1, 'Key is required')
  .max(120)
  .regex(/^[a-z0-9._-]+$/, 'Use lowercase letters, numbers, dots, underscores, hyphens');

export const SecretListItemSchema = z.object({
  id: UuidSchema,
  key: z.string(),
  label: z.string(),
  description: z.string().nullable(),
  kind: SecretKindSchema,
  provider: z.string().nullable(),
  providerType: SecretProviderTypeSchema.nullable(),
  environment: SecretEnvironmentSchema,
  isActive: z.boolean(),
  /** Whether a value is stored. The plaintext itself is never returned here. */
  hasValue: z.boolean(),
  /** Masked hint - last 4 chars only, e.g. "••••a1b2". Never the full value. */
  valuePreview: z.string().nullable(),
  lastRotatedAt: IsoDateTimeSchema.nullable(),
  createdAt: IsoDateTimeSchema,
});
export type SecretListItem = z.infer<typeof SecretListItemSchema>;

export const SecretListQuerySchema = PaginationQuerySchema.extend({
  provider: z.string().trim().min(1).max(80).optional(),
  providerType: SecretProviderTypeSchema.optional(),
  kind: SecretKindSchema.optional(),
  environment: SecretEnvironmentSchema.optional(),
  isActive: BooleanQueryParamSchema.optional(),
  includeDeleted: BooleanQueryParamSchema.optional(),
});
export type SecretListQuery = z.infer<typeof SecretListQuerySchema>;

export const SecretListResponseSchema = paginatedResponseSchema(SecretListItemSchema);
export type SecretListResponse = z.infer<typeof SecretListResponseSchema>;

export const SecretDetailSchema = SecretListItemSchema.extend({
  updatedAt: IsoDateTimeSchema,
  deletedAt: IsoDateTimeSchema.nullable(),
});
export type SecretDetail = z.infer<typeof SecretDetailSchema>;

const SecretMetaSchema = z.object({
  key: SecretKeySchema,
  label: z.string().trim().min(1, 'Label is required').max(160),
  description: z.string().trim().max(500).nullable().optional(),
  kind: SecretKindSchema.optional(),
  provider: z.string().trim().max(80).nullable().optional(),
  providerType: SecretProviderTypeSchema.nullable().optional(),
  environment: SecretEnvironmentSchema.optional(),
  isActive: z.boolean().optional(),
});

const SecretValueSchema = z.string().min(1, 'Value is required').max(8192);

export const CreateSecretRequestSchema = SecretMetaSchema.extend({ value: SecretValueSchema });
export type CreateSecretRequest = z.infer<typeof CreateSecretRequestSchema>;

/** Partial metadata; `value` omitted = keep the existing ciphertext. */
export const UpdateSecretRequestSchema = SecretMetaSchema.partial().extend({
  value: SecretValueSchema.optional(),
});
export type UpdateSecretRequest = z.infer<typeof UpdateSecretRequestSchema>;

/** Response of the audited reveal endpoint - the only place the plaintext appears. */
export const SecretRevealResponseSchema = z.object({
  id: UuidSchema,
  key: z.string(),
  value: z.string(),
});
export type SecretRevealResponse = z.infer<typeof SecretRevealResponseSchema>;
